import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios from 'axios';
import getAuthSession from '../../services/auth';
import { Workflow, WorkflowAssessmentResponse } from '../../interfaces/workflow.interface';
import { Survey } from '../../interfaces/survey.interface';
import { Assessment } from '../../interfaces/assessment.interface';
import { Questionnaire } from '../../interfaces/questionnaire.interface';
import { WORKFLOW_STATUSES, fetchWorkflowById, wfHierarchy } from '../../services/workflow';
import { QuestionnaireTypes } from '../../services/questionnaires';
import { createAssessment } from './assessmentSlice';
import { setAssessmentStatus } from './questionnaireAnswerSlice';

interface InitialWorkflowState {
  workflows: Workflow[];
  workflow?: Workflow;
  baseline?: Survey;
}

const initialState: InitialWorkflowState = {
  workflow: undefined,
  workflows: [],
  baseline: undefined,
};

const baseUrl = process.env.REACT_APP_BASE_API;

/** Extracts an ID from a field that may be an object with an `id` property or already an ID. */
function extractId(field: unknown): number | null {
  if (field === null || field === undefined) return null;
  if (typeof field === 'object' && 'id' in field) {
    return (field as { id: number }).id;
  }
  return field as number;
}

/** Converts a WorkflowAssessmentResponse to a Workflow by extracting IDs from nested objects. */
function toWorkflowForStore(response: WorkflowAssessmentResponse): Workflow {
  return {
    ...response,
    assessment: response.assessment?.id ?? null,
    assessmentSurvey: response.assessmentSurvey?.id ?? null,
    baseline: extractId(response.baseline),
    bpSurvey: extractId(response.bpSurvey),
    site: extractId(response.site),
    serviceProvider: extractId(response.serviceProvider),
    assessor: extractId(response.assessor),
    assignedAssessor: extractId(response.assignedAssessor),
  } as Workflow;
}

export type FetchAllWorkflowsRequest = {
  created_at?: string;
  updated_at?: string;
  baseline?: number;
  bp_survey?: number;
  assessment?: number;
  assessment_survey?: number;
  service_provider?: number;
  assessor?: number;
  assigned_assessor?: number;
  site?: number;
  status?: string;
  workflow_type?: number;
  application?: number;
  version?: number;
  is_active?: boolean;
  badges?: string;
  site_ids?: string;
  application_version_ids?: string;
  search?: string;
  ordering?: string;
  limit?: number;
  offset?: number;
};

export const fetchAllWorkflows = createAsyncThunk(
  'workflow/fetchAllWorkflows',
  async ({ limit = 9999, offset = 0, ...rest }: FetchAllWorkflowsRequest, { rejectWithValue }) => {
    try {
      const authSession = await getAuthSession();
      const queryString = Object.entries(rest).filter(([_, val]) => val !== undefined && (_ || !_)).map(([key, value]) => `${key}=${value}`).join('&');
      let url = `${baseUrl}/workflows/?offset=${offset}&limit=${limit}${queryString ? `&${queryString}` : ''}`;
      const response = await axios.get(
        url,
        {
          headers: { 'Authorization': `Bearer ${authSession.getIdToken().getJwtToken()}` },
        },
      );
      return response.data.results as Workflow[];
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const fetchWorkflowByIdSlice = createAsyncThunk(
  'workflow/fetchWorkflowById',
  async (id: number, { rejectWithValue }) => {
    try {
      const resp = await fetchWorkflowById(id);
      return resp as Workflow;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const fetchWorkflow = createAsyncThunk(
  'workflow/fetchWorkflow',
  async (request: { survey: Survey, assessment?: Assessment }, { rejectWithValue }) => {
    try {
      const { survey, assessment } = request;
      const authSession = await getAuthSession();
      let url = `${baseUrl}/workflows`;
      if (assessment) {
        url += `/?bp_survey=${survey.id}&assessment=${assessment.id}`;
      } else {
        switch ((survey.questionnaire as Questionnaire).type) {
          case QuestionnaireTypes.APPLICATION_BASELINE:
          case QuestionnaireTypes.SITE_BASELINE:
            url += `/?baseline=${survey.id}`;
            break;
          case QuestionnaireTypes.MPA_BEST_PRACTICE:
            url += `/?bp_survey=${survey.id}`;
            break;
        }
      }
      url += '&ordering=-created_at';

      const response = await axios.get(
        url,
        {
          headers: { 'Authorization': `Bearer ${authSession.getIdToken().getJwtToken()}` },
        },
      );
      return response.data.results[0] as Workflow;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const fetchBaselineSurvey = createAsyncThunk(
  'workflow/fetchBaselineSurvey',
  async (request: { id: number }, { rejectWithValue }) => {
    try {
      const { id } = request;
      const authSession = await getAuthSession();

      let url = `${baseUrl}/surveys/${id}`;

      const response = await axios.get(
        url,
        {
          headers: { 'Authorization': `Bearer ${authSession.getIdToken().getJwtToken()}` },
        },
      );

      return response.data as Survey;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const updateWorkflow = createAsyncThunk(
  'workflow/updateWorkflow',
  async (request: Partial<Workflow> & { adminOverride?: boolean }, { rejectWithValue }) => {
    try {
      const { id } = request;
      let req = { ...request };
      const authSession = await getAuthSession();
      // Revert button includes adminOverride
      if (!req.adminOverride) {
        const currentWorkflow = await axios.get(
          `${process.env.REACT_APP_BASE_API}/workflows/${id}/`,
          {
            headers: { 'Authorization': `Bearer ${authSession.getIdToken().getJwtToken()}` },
          },
        );

        const currentStatus = currentWorkflow.data?.status;

        const currentStatusIndex = wfHierarchy.indexOf(currentStatus || '');
        const newStatusIndex = wfHierarchy.indexOf(req.status as typeof wfHierarchy[number] || '');
        const statusExceptions = req.status === WORKFLOW_STATUSES.PRE_ASSESSMENT_INCOMPLETE || (currentStatus === 'complete' && req.status === 'incomplete') || (currentStatus === 'non-compliant' && req.status === 'incomplete');
        // If the new status is lower in the hierarchy, just use the current status unless in pre-assessment phase
        if (newStatusIndex < currentStatusIndex && !statusExceptions) {
          req.status = currentStatus;
        }
      }
      const response = await axios.patch(
        `${baseUrl}/workflows/${id}/`, req,
        {
          headers: { 'Authorization': `Bearer ${authSession.getIdToken().getJwtToken()}` },
        },
      );
      return response.data as Workflow;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const assignAssessor = createAsyncThunk(
  'workflow/assignAssessor',
  async (
    request: {
      assessorID: number;
      workflowId: number;
      assignedAssessorId: number;
      isOnPremise: boolean;
      isCloud: boolean;
      isInPerson: boolean;
    },
    thunkAPI,
  ) => {
    try {
      const { workflowId } = request;

      const authSession = await getAuthSession();

      const response = await axios.post(
        `${process.env.REACT_APP_BASE_API}/assign-assessor/${workflowId}/`,
        { ...request },
        {
          headers: {
            Authorization: `Bearer ${authSession.getIdToken().getJwtToken()}`,
          },
        },
      );

      const responseWorkflow: WorkflowAssessmentResponse = response.data;

      thunkAPI.dispatch(
        createAssessment.fulfilled(
          responseWorkflow.assessment,
          thunkAPI.requestId,
          { ...responseWorkflow.assessment },
        ),
      );

      return toWorkflowForStore(responseWorkflow);
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response?.data);
    }
  },
);

export const acceptAssessment = createAsyncThunk(
  'workflow/acceptAssessment',
  async (workflowId: number, thunkAPI) => {
    try {
      const authSession = await getAuthSession();

      const response = await axios.post(
        `${process.env.REACT_APP_BASE_API}/accept-assessment/${workflowId}/`,
        {},
        {
          headers: {
            Authorization: `Bearer ${authSession.getIdToken().getJwtToken()}`,
          },
        },
      );

      const responseWorkflow: WorkflowAssessmentResponse = response.data;

      return { workflow: toWorkflowForStore(responseWorkflow), assessment: responseWorkflow.assessment };
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response?.data);
    }
  },
);

export const beginAssessment = createAsyncThunk(
  'workflow/beginAssessment',
  async (workflowId: number, thunkAPI) => {
    try {
      const authSession = await getAuthSession();

      const response = await axios.post(
        `${process.env.REACT_APP_BASE_API}/begin-assessment/${workflowId}/`,
        {},
        {
          headers: {
            Authorization: `Bearer ${authSession.getIdToken().getJwtToken()}`,
          },
        },
      );

      const responseWorkflow: WorkflowAssessmentResponse = response.data;

      return { workflow: toWorkflowForStore(responseWorkflow), assessmentSurvey: responseWorkflow.assessmentSurvey };
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response?.data);
    }
  },
);

export const rejectAssessment = createAsyncThunk(
  'workflow/rejectAssessment',
  async (workflowId: number, thunkAPI) => {
    try {
      const authSession = await getAuthSession();

      const response = await axios.delete(
        `${process.env.REACT_APP_BASE_API}/reject-assessment/${workflowId}/`,
        {
          headers: {
            Authorization: `Bearer ${authSession.getIdToken().getJwtToken()}`,
          },
        },
      );

      const responseWorkflow: WorkflowAssessmentResponse = response.data;

      return toWorkflowForStore(responseWorkflow);
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response?.data);
    }
  },
);

export const updateAssessor = createAsyncThunk(
  'workflow/updateAssessor',
  async (
    request: {
      assessorID: number;
      workflowId: number;
      assignedAssessorId: number;
      isOnPremise: boolean;
      isCloud: boolean;
      isInPerson: boolean;
    },
    thunkAPI,
  ) => {
    try {
      const { workflowId } = request;

      const authSession = await getAuthSession();

      const response = await axios.patch(
        `${process.env.REACT_APP_BASE_API}/update-assessor/${workflowId}/`,
        { ...request },
        {
          headers: {
            Authorization: `Bearer ${authSession.getIdToken().getJwtToken()}`,
          },
        },
      );

      const responseWorkflow: WorkflowAssessmentResponse = response.data;

      return toWorkflowForStore(responseWorkflow);
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response?.data);
    }
  },
);

export const submitAssessment = createAsyncThunk(
  'workflow/submitAssessment',
  async (workflowId: number, thunkAPI) => {
    try {
      const authSession = await getAuthSession();

      const response = await axios.post(
        `${process.env.REACT_APP_BASE_API}/submit-assessment/${workflowId}/`,
        {},
        {
          headers: {
            Authorization: `Bearer ${authSession.getIdToken().getJwtToken()}`,
          },
        },
      );

      const responseWorkflow: WorkflowAssessmentResponse = response.data;

      thunkAPI.dispatch(setAssessmentStatus('Submitted'));

      return { workflow: toWorkflowForStore(responseWorkflow), assessmentSurvey: responseWorkflow.assessmentSurvey };
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response?.data);
    }
  },
);

function upsertWorkflow(state: InitialWorkflowState, updated: Workflow) {
  if (!state.workflows) state.workflows = []; // ensure array
  const index = state.workflows.findIndex(wf => wf.id === updated.id);
  if (index >= 0) {
    // Merge existing workflow with updated data to preserve fields not in the API response
    state.workflows[index] = { ...state.workflows[index], ...updated };
    state.workflow = state.workflows[index];
  } else {
    state.workflows.push(updated);
    state.workflow = updated;
  }
}

const workflowSlice = createSlice({
  name: 'workflow',
  initialState,
  reducers: {
    resetWorkflow: (state) => {
      state.baseline = undefined;
      state.workflows = [];
      state.workflow = undefined;
    },
  },
  extraReducers: builder => {
    // for fetching LIST of workflows
    builder.addCase(fetchAllWorkflows.fulfilled, (state, { payload }: PayloadAction<Workflow[]>) => {
      state.workflows = payload;
    });

    // for fetching SINGLE whole workflow data
    builder.addCase(fetchWorkflow.fulfilled, (state, { payload }: PayloadAction<Workflow>) => {
      state.workflow = payload;
    });

    // for fetching SINGLE workflow by ID
    builder.addCase(fetchWorkflowByIdSlice.fulfilled, (state, { payload }: PayloadAction<Workflow>) => {
      state.workflow = payload;
    });

    // for updating workflow
    builder.addCase(updateWorkflow.fulfilled, (state, { payload }) => {
      upsertWorkflow(state, payload);
    });

    builder.addCase(assignAssessor.fulfilled, (state, { payload }) => {
      upsertWorkflow(state, payload);
    });

    builder.addCase(updateAssessor.fulfilled, (state, { payload }) => {
      upsertWorkflow(state, payload);
    });

    builder.addCase(acceptAssessment.fulfilled, (state, { payload }) => {
      upsertWorkflow(state, payload.workflow);
    });

    builder.addCase(beginAssessment.fulfilled, (state, { payload }) => {
      upsertWorkflow(state, payload.workflow);
    });

    builder.addCase(rejectAssessment.fulfilled, (state, { payload }) => {
      upsertWorkflow(state, payload);
    });

    builder.addCase(submitAssessment.fulfilled, (state, { payload }) => {
      upsertWorkflow(state, payload.workflow);
    });

    // for baseline
    builder.addCase(fetchBaselineSurvey.fulfilled, (state, { payload }: PayloadAction<Survey | undefined>) => {
      state.baseline = payload;
    });
  },
});

export const { resetWorkflow } = workflowSlice.actions;

export default workflowSlice.reducer;
